import {BuyerServerTransactionStatus} from '@wix/cashier-common/dist/src/enums/transactions/BuyerServerTransactionStatus';
import {ICashierPaymentsProps} from '@wix/cashier-payments-widget';
import {PaymentFailResult} from '@wix/cashier-payments-widget/src/sdk/types/PaymentCompleteResult';
import {WixSdk} from '@wix/js-sdk-wrapper';
import {DetailedError} from '@wix/music-shared/dist/src/logging/DetailedError';
import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getLocale} from '../../services';
import {BI_ERRORS} from '../../services/biErrorLogger/biErrors';
import {cashierError} from '../../services/cashier/cashierActions';
import {ServerTransactionStatus} from '../../services/cashier/common';
import {getMetaSiteId} from '../../services/env/getMetaSiteId';
import {errorLogger} from '../../services/errorLogger/errorLogger';
import {
  cashierMandatoryFieldsAction,
  cashierWidgetLoadStateAction,
  payWithCashierCanceledAction,
  payWithCashierFailureAction,
  payWithCashierPendingAction,
  payWithCashierSuccessAction,
} from '../../store/actions';
import {emailSelector, mandatoryFieldsSelector, snapshotIdSelector} from '../../store/selectors';
import {currencySelector, priceSelector} from '../../store/selectors/price.selector';

function getOnError(dispatch): ICashierPaymentsProps['onError'] {
  const onError: ICashierPaymentsProps['onError'] = (paymentMethod, paymentError) => {
    if (paymentError && paymentError.type === 'PAYMENT_ERROR') {
      return;
    }
    console.error('onError', paymentMethod, paymentError);
    errorLogger.logError(
      {
        bi: BI_ERRORS.getWindow.cashier.genericError,
        biParams: [paymentError && paymentError.type],
      },
      {
        reason: new DetailedError('Payment Has Filed', paymentError),
        paymentMethod,
      },
    );

    dispatch(cashierError());
  };
  return onError;
}

function getOnPaymentComplete(dispatch): ICashierPaymentsProps['onPaymentComplete'] {
  const onPaymentComplete: ICashierPaymentsProps['onPaymentComplete'] = (paymentMethod, result) => {
    if (process.env.NODE_ENV !== 'test') {
      console.info(`onPaymentComplete`, paymentMethod, result.status, result.clientStatus, result);
    }

    switch (result.status) {
      case BuyerServerTransactionStatus.Declined:
      case 'EXPIRED' as BuyerServerTransactionStatus: // I don't know if it's the case, but doesn't match new types
      case BuyerServerTransactionStatus.Undefined:
      case BuyerServerTransactionStatus.Timeout:
      case BuyerServerTransactionStatus.Failed:
        dispatch(
          payWithCashierFailureAction({
            errorMessage:
              result.clientStatus === 'Failed'
                ? // not sure why I need casting and why it's not narrowing the type here
                  (result as PaymentFailResult).translatedError
                : 'Oops, something went wrong',
          }),
        );
        break;
      case BuyerServerTransactionStatus.Approved:
      case 'OK':
        dispatch(payWithCashierSuccessAction());
        break;
      case BuyerServerTransactionStatus.BuyerCanceled:
      case ServerTransactionStatus.TpaCanceled as unknown as BuyerServerTransactionStatus:
        dispatch(payWithCashierCanceledAction());
        break;
      default:
        if (result.isPending) {
          dispatch(payWithCashierPendingAction());
          break;
        }
        // TODO: add snapshotId?
        errorLogger.logError(
          {
            bi: BI_ERRORS.getWindow.cashier.unknownStatus,
            biParams: [result.status /* snapshotId*/],
          },
          {result /* snapshotId*/},
        );
        console.error('UNKNOWN STATUS', paymentMethod, result);
        dispatch(payWithCashierFailureAction({errorMessage: 'Oops, something went wrong'}));
        break;
    }
  };
  return onPaymentComplete;
}

export function useCashierProps() {
  const dispatch = useDispatch();
  const email = useSelector(emailSelector);
  const snapshotId = useSelector(snapshotIdSelector);
  const onError = getOnError(dispatch);
  const onPaymentComplete = getOnPaymentComplete(dispatch);
  const [isLoading, setLoading] = useState(true);
  const loadingStateRef = useRef(new Set());
  const mandatoryFieldsPrefilled = useSelector(mandatoryFieldsSelector);
  const currency = useSelector(currencySelector);
  const price = useSelector(priceSelector).toFixed(2);

  useEffect(() => {
    dispatch(cashierWidgetLoadStateAction(isLoading));
  }, [isLoading]);

  const props: ICashierPaymentsProps = {
    debug: true,
    configuration: {
      currency,
      amount: price,
      visitorId: WixSdk.Utils.getInstanceValue('aid'),
      locale: getLocale(),
      appId: WixSdk.Utils.getInstanceValue('appDefId'),
      appInstanceId: WixSdk.Utils.getInstanceId(),
      snapshotId,
      msid: getMetaSiteId(),
    },
    mandatoryFields: {
      email,
    },
    mandatoryFieldsPrefilled,
    walletPaymentInNewTab: true,
    onError,
    onPaymentComplete,
    onPaymentStart() {
      try {
        const mandatoryFields = getMandatoryFields();
        dispatch(cashierMandatoryFieldsAction(mandatoryFields));
      } catch (reason) {
        errorLogger.logError(
          {
            bi: BI_ERRORS.getWindow.cashier.storeMandatoryFields,
            biParams: [reason && reason.type],
          },
          {reason},
        );
      }
    },
    onLoadingChanged(key: string, isLoading: boolean): void {
      const loadingState = loadingStateRef.current;
      if (isLoading) {
        loadingState.add(key);
      } else {
        loadingState.delete(key);
      }
      setLoading(loadingState.size !== 0);
    },
  };
  return {isLoading, props};
}

function getMandatoryFields() {
  const cashierPayments = window.cashierPayments;
  if (!cashierPayments) {
    throw new Error('cashierPayments is not in window');
  }
  if (!cashierPayments.getMandatoryFieldsMap) {
    throw new Error('getMandatoryFieldsMap is not in cashierPayments');
  }
  if (typeof cashierPayments.getMandatoryFieldsMap !== 'function') {
    throw new Error('cashierPayments.getMandatoryFieldsMap is not function');
  }
  const fields = cashierPayments.getMandatoryFieldsMap();
  if (!fields) {
    throw new Error(`cashierPayments.getMandatoryFieldsMap returned ${typeof fields}`);
  }
  return fields;
}
